// src/components/map/hooks/adapters/useRigFracLayerFilter.ts
//
// Adapter hook: Rig/Frac layer-local attribute filtering (EVA-63), session-only
// value hiding (EVA-64), AND committed-well polygon scoping (EVA-66).
//
// The SOLE setFilter writer for RIG_LAYER/FRAC_LAYER — composes every concern
// into one expression per layer:
//   ["all", <type clause>,
//           ...attribute-inclusion clauses (EVA-63),
//           <hidden-values exclusion clause if any> (EVA-64),
//           <committed-selection `within` clause if any> (EVA-66),
//           <exclude-polygons `!within` clause if any> (EVA-66)]
// These concerns used to be written by separate hooks that each called
// setFilter independently, so the last one to run clobbered the others
// (last-writer-wins). In particular useRigFracPolygonMapFilter re-fired on
// every pan — panning dispatches filterWellsFromMapExtent, which updates
// state.filter.polygonFilter, so that hook rewrote the layer filter down to
// the bare type clause and dropped any active attribute selection. Owning all
// concerns here removes that clobber. useRigFracColorBy.ts keeps ownership of
// the icon-color PAINT property only.
//
// Re-applies each layer's composed filter whenever filter selections, hidden
// values, the committed polygon filter, or the project layer list change, AND
// whenever the layers are (re)added after a style reload. getMapLayers.ts only
// bakes the bare rig-vs-frac type clause into layer.filter
// (RIG_TYPE_CLAUSE/FRAC_TYPE_CLAUSE), so this hook is what layers on any active
// attribute selection / hidden values / polygon scope on top. The
// `isMapInitialized` signal (flips true→false→true across the style.load →
// waiting → addMapboxLayers pipeline in useMapLifecycle.ts) mirrors the same
// re-apply trigger useMapTileSourceManager uses for WELL_LAYER/FACILITY_LAYER,
// so filters survive a style reload without editing useMapLifecycle directly.
import * as Sentry from "@sentry/react";
import { MutableRefObject, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import {
  FRAC_LAYER,
  FRAC_TYPE_CLAUSE,
  RIG_FRAC_LAYER_TYPE,
  RIG_FRAC_SOURCE_LAYER,
  RIG_LAYER,
  RIG_TYPE_CLAUSE
} from "constants/mapLayers.constants";
import mapboxgl from "mapbox-gl";
import { RootState } from "store/rootReducer";
import {
  selectHiddenRigFracValues,
  selectLayerFilterSettings,
  selectRigFracColorByDetached,
  selectRigFracFocusColorBy,
  useMapStore
} from "stores/map";
import { normalizeMatchKey } from "utils/color/focusColorSync/operatorKeys";
import {
  FilterExpression,
  buildMainFilterConstraintClauses,
  buildRigFracFilter
} from "utils/map/buildRigFracFilter";
import {
  MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER,
  MainFilterRigFracAttribute,
  MainFilterRigFracConstraints
} from "utils/map/mainFilterRigFrac";
import {
  RIG_FRAC_NO_VALUE_OPTION,
  RigFracRow,
  computeRigFracFacets
} from "utils/map/rigFracFacets";
import {
  hasPolygonCoordinates,
  isCommittedSelectionPolygon,
  polygonFilterToGeometry,
  unionExcludePolygons
} from "utils/polygonFilterGeometry";

import { useMainFilterRigFracConstraints } from "hooks/rigfrac/useMainFilterRigFracConstraints";

import { resolveEffectiveColorByField } from "components/project/layers/components/rig-frac-details/rigFracColorByFields";
import {
  IProjectLayer,
  useProjectLayerListQuery
} from "components/project/layers/queries";
import { useProjectContext } from "components/project/projects/context";

export interface UseRigFracLayerFilterParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Whether the map has completed its (re-)initialization pipeline. */
  readonly isMapInitialized: boolean;
  // Gated by the "Rig And Frac Layers" beta flag at the mount site.
  readonly enabled?: boolean;
}

type FoldedMainFilterConstraint = { include?: Set<string>; exclude?: Set<string> };
type FoldedMainFilterConstraints = Partial<
  Record<MainFilterRigFracAttribute, FoldedMainFilterConstraint>
>;

// Case-folded once per syncFacets run (not per row/feature) via normalizeMatchKey
// — the same rule the colour half matches with — so this can't drift from
// buildMainFilterConstraintClauses's Mapbox-side comparison.
function foldMainFilterConstraints(
  constraints: MainFilterRigFracConstraints
): FoldedMainFilterConstraints {
  const folded: FoldedMainFilterConstraints = {};
  for (const attribute of MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER) {
    const constraint = constraints[attribute];
    if (!constraint) continue;
    folded[attribute] = {
      include: constraint.include && new Set(constraint.include.map(normalizeMatchKey)),
      exclude: constraint.exclude && new Set(constraint.exclude.map(normalizeMatchKey))
    };
  }
  return folded;
}

// EVA-172. A missing value fails an `include` (nothing to include) and
// passes an `exclude` (nothing to exclude).
function rowPassesMainFilterConstraints(
  row: RigFracRow,
  foldedConstraints: FoldedMainFilterConstraints
): boolean {
  for (const attribute of MAIN_FILTER_CONSTRAINT_ATTRIBUTE_ORDER) {
    const constraint = foldedConstraints[attribute];
    if (!constraint) continue;
    const value = row[attribute];
    const folded = value == null ? undefined : normalizeMatchKey(value);
    if (constraint.include && (folded == null || !constraint.include.has(folded))) {
      return false;
    }
    if (constraint.exclude && folded != null && constraint.exclude.has(folded)) {
      return false;
    }
  }
  return true;
}

export function useRigFracLayerFilter({
  mapboxRef,
  isMapInitialized,
  enabled = true
}: UseRigFracLayerFilterParams): void {
  const layerFilterSettings = useMapStore(selectLayerFilterSettings);
  const hiddenByLayer = useMapStore(selectHiddenRigFracValues);
  const focusColorBy = useMapStore(selectRigFracFocusColorBy);
  const colorByDetached = useMapStore(selectRigFracColorByDetached);
  const setRigFracFilterOptions = useMapStore((s) => s.setRigFracFilterOptions);
  // EVA-66: the committed well polygon filter that scopes the rig/frac data
  // table and well icons also scopes these map icon layers. Read from the well
  // filter tree (state.filter), matching the retired useRigFracPolygonMapFilter.
  const polygonFilter = useSelector((state: RootState) => state.filter.polygonFilter);
  const excludePolygonsFilter = useSelector(
    (state: RootState) => state.filter.excludePolygonsFilter
  );
  const { constraints } = useMainFilterRigFracConstraints();
  const foldedConstraints = useMemo(
    () => foldMainFilterConstraints(constraints),
    [constraints]
  );
  const { selectedProjectId } = useProjectContext();

  const layerList = useProjectLayerListQuery({
    projectId: selectedProjectId,
    isAutoRefetching: enabled && !!selectedProjectId
  });
  const layers = layerList.data;

  // Derive each field's still-available options (EVA-63 revision) from the rig/frac
  // features currently on the map, so a dropdown offers only values reachable under
  // the OTHER active selections (cascade) — not the whole market domain. Pure client:
  // reads the loaded `eva-rigfrac` vector features (RIG_LAYER is the source id; FRAC
  // reuses it), splits by feature `type`, and writes the facets to the store for the
  // popover.
  //
  // Options only — the user's existing selections are NEVER pruned here: the feature
  // set is viewport/tile-scoped, so a value the user picked can be absent from the
  // currently-loaded features (off-screen / not-yet-loaded) without being invalid.
  // The popover unions the active selections into the shown options so they persist.
  // Runs once the source exists; before that it bails (the popover stays "Loading…").
  const syncFacets = useCallback(() => {
    const mapbox = mapboxRef.current;
    if (!enabled || !mapbox || typeof mapbox.querySourceFeatures !== "function") return;
    if (!mapbox.getSource(RIG_LAYER)) return;
    // Only compute once the source's tiles have actually loaded — otherwise
    // querySourceFeatures returns [] simply because nothing is loaded yet, and
    // writing that would flip the popover to "No options" mid-load. Bailing
    // here keeps it on "Loading…" until the data has settled.
    if (
      typeof mapbox.isSourceLoaded === "function" &&
      !mapbox.isSourceLoaded(RIG_LAYER)
    ) {
      return;
    }

    let features: mapboxgl.MapboxGeoJSONFeature[];
    try {
      features = mapbox.querySourceFeatures(RIG_LAYER, {
        sourceLayer: RIG_FRAC_SOURCE_LAYER
      });
    } catch {
      return;
    }

    // No id-dedupe needed: querySourceFeatures repeats a feature once per clipping
    // tile, but computeRigFracFacets accumulates each field into a Set, so repeats
    // don't skew the distinct-value results.
    const rowsByLayer: Record<string, RigFracRow[]> = {
      [RIG_LAYER]: [],
      [FRAC_LAYER]: []
    };
    for (const feature of features) {
      const props = feature.properties ?? {};
      const layerId =
        props.type === "Frac" ? FRAC_LAYER : props.type === "Rig" ? RIG_LAYER : null;
      if (!layerId) continue;
      const row: RigFracRow = {
        operator: props.operator,
        county: props.county,
        basin: props.basin,
        subBasin: props.subBasin,
        locationType: props.locationType,
        stateProvince: props.stateProvince,
        country: props.country,
        // EVA-107: the tile omits this tag when the source has no trajectory, and
        // computeRigFracFacets drops nullish values — so without substituting the
        // "n/a" option here, frac crews and unclassified rigs would be invisible
        // to the filter rather than selectable. buildRigFracFilter maps a
        // selection of it back to "tag absent".
        rigTrajectory: props.rigTrajectory ?? RIG_FRAC_NO_VALUE_OPTION
      };
      // EVA-172: querySourceFeatures ignores the layer's Mapbox filter, so
      // without this the facet dropdowns would keep offering values the main
      // filter has already excluded.
      if (!rowPassesMainFilterConstraints(row, foldedConstraints)) continue;
      rowsByLayer[layerId].push(row);
    }

    // Write facets for BOTH layers, even when a layer has no features in view:
    // an empty facet set resolves the popover to "No options" rather than a
    // perpetual "Loading…". Once the source exists (map ready), the store slice
    // is always present. Options-only — selections are never pruned.
    for (const layerId of [RIG_LAYER, FRAC_LAYER]) {
      const selections = layerFilterSettings[layerId] ?? {};
      setRigFracFilterOptions(
        layerId,
        computeRigFracFacets(rowsByLayer[layerId], selections)
      );
    }
  }, [
    enabled,
    mapboxRef,
    layerFilterSettings,
    setRigFracFilterOptions,
    foldedConstraints
  ]);

  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!enabled || !mapbox) {
      return;
    }

    const rigFracLayers = (layers ?? []).filter(
      (layer) => layer.projectLayerType === RIG_FRAC_LAYER_TYPE
    );

    // The EVA-64 hidden-values exclusion applies to the layer's active color-by
    // attribute. resolveEffectiveColorByField always resolves to a real field (the
    // retired "none" sentinel now maps to the Current Operator default), so the
    // exclusion is contributed whenever that layer has any hidden values.
    const buildExclusion = (mapLayerId: string): FilterExpression | undefined => {
      const layer = rigFracLayers.find((l: IProjectLayer) => l.name === mapLayerId);
      const field = resolveEffectiveColorByField({
        saved: layer?.colorByField,
        focusColorBy,
        isDetached: !!layer && !!colorByDetached[layer.layerId],
        layerName: mapLayerId
      });
      const hidden = (layer && hiddenByLayer[layer.layerId]) ?? [];
      return hidden.length > 0
        ? ["!", ["in", ["get", field], ["literal", hidden]]]
        : undefined;
    };

    // EVA-66 polygon scoping — identical for both layers (not attribute- or
    // rig-vs-frac-specific). A `within` clause is added ONLY for a committed
    // lasso selection (source === "selection"), never for the viewport-extent
    // default that panning writes to state.filter.polygonFilter — that is
    // exactly why a plain pan must NOT restrict, and must NOT drop, the
    // attribute filter. Degenerate (empty-ring) geometries are skipped because
    // mapbox-gl's `within` throws on them.
    const polygonClauses: FilterExpression[] = [];
    if (isCommittedSelectionPolygon(polygonFilter)) {
      const geometry = polygonFilterToGeometry(polygonFilter);
      if (hasPolygonCoordinates(geometry)) {
        polygonClauses.push(["within", geometry]);
      }
    }
    const excludePolygon = unionExcludePolygons(excludePolygonsFilter);
    if (excludePolygon && hasPolygonCoordinates(excludePolygon)) {
      polygonClauses.push(["!", ["within", excludePolygon]]);
    }

    // EVA-172 main-filter constraints — identical for both layers (not
    // attribute- or rig-vs-frac-specific); AND on top of each layer's own
    // EVA-63 selections.
    const mainFilterClauses = buildMainFilterConstraintClauses(constraints);

    // Guarded per layer — layers only exist when the "Rig And Frac Layers"
    // beta flag is on (and only after addMapboxLayers has run).
    //
    // setFilter is wrapped in try/catch: the composed expression now includes a
    // displaySettings blob round-tripped through eva-project-svc, which does not
    // validate it, and mergeDisplaySettings guards only the record SHAPE, not
    // nested value types. A malformed saved value (e.g. a non-array
    // layerFilterSettings[layer].operator) would make mapbox-gl throw inside this
    // effect and unmount the surrounding Map subtree. Swallow + report so a bad
    // blob degrades to "filter not applied" instead of crashing the map.
    if (mapbox.getLayer(RIG_LAYER)) {
      try {
        mapbox.setFilter(
          RIG_LAYER,
          buildRigFracFilter(RIG_TYPE_CLAUSE, layerFilterSettings[RIG_LAYER] ?? {}, [
            buildExclusion(RIG_LAYER),
            ...polygonClauses,
            ...mainFilterClauses
          ])
        );
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("useRigFracLayerFilter", { layer: RIG_LAYER });
          scope.setLevel("warning");
          Sentry.captureException(error);
        });
      }
    }
    if (mapbox.getLayer(FRAC_LAYER)) {
      try {
        mapbox.setFilter(
          FRAC_LAYER,
          buildRigFracFilter(FRAC_TYPE_CLAUSE, layerFilterSettings[FRAC_LAYER] ?? {}, [
            buildExclusion(FRAC_LAYER),
            ...polygonClauses,
            ...mainFilterClauses
          ])
        );
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("useRigFracLayerFilter", { layer: FRAC_LAYER });
          scope.setLevel("warning");
          Sentry.captureException(error);
        });
      }
    }

    // Recompute available options after the (re-)applied filter selections.
    syncFacets();
  }, [
    mapboxRef,
    isMapInitialized,
    layerFilterSettings,
    hiddenByLayer,
    focusColorBy,
    colorByDetached,
    polygonFilter,
    excludePolygonsFilter,
    constraints,
    layers,
    enabled,
    syncFacets
  ]);

  // Recompute options once tiles settle (pan/zoom/initial load) — querySourceFeatures
  // only sees loaded tiles, so "current rig/frac" tracks what's actually on the map.
  // Depends on isMapInitialized: useMapLifecycle assigns mapboxRef.current AFTER this
  // hook runs on first mount, so without that signal the listener would never attach
  // until an unrelated re-render — options wouldn't track pan/zoom on initial load.
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!enabled || !mapbox || typeof mapbox.on !== "function") {
      return;
    }
    const onIdle = (): void => syncFacets();
    mapbox.on("idle", onIdle);
    return () => {
      mapbox.off("idle", onIdle);
    };
  }, [enabled, mapboxRef, isMapInitialized, syncFacets]);
}
